import { recipientIds, normalizeRecipients } from "../../utils/availabilityRecipients";
import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Check,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Heart,
  X,
  ChevronDown,
  ChevronUp,
  Bone,
} from "lucide-react";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import "./TeamPawsAvailabilityPage.css";
import apiClient from "../../api/apiClient";
import { useGlobalAlert } from "../../context/AlertContext";
import TopNavBar from "../../components/TopNavBar";
import userPlaceholder from "../../assets/userPlaceholder.png";
import appIcon from "../../assets/socialPawsLogo.svg";
import MobileTopBar from "../../components/MobileTopBar";
import morningIconSvg from "../../assets/morning.svg";
import afternoonIconSvg from "../../assets/afternoon.svg";
import eveningIconSvg from "../../assets/evening.svg";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const shareOptionsMap = {
  none: "No one",
  selected: "Selected Pup Pals/ Pups",
  all: "All Trusted Pup Pals/ Pups",
};

const TeamPawsAvailabilityPage = () => {
  const navigate = useNavigate();
  const { triggerAlert } = useGlobalAlert();
  const location = useLocation();
  const { profile } = useSelector((state) => state.user);

  const stateData = location.state || {};
  const partnerData = stateData.partnerData || null;
  const partnerId = stateData.partnerId || null;
  const partnerName = stateData.partnerName || "Partner";
  const bookingId = stateData.bookingId || null;

  const walkerUserId = partnerData?.partnerId || partnerId || null;

  const today = useMemo(() => new Date(), []);
  const todayDayjs = useMemo(() => dayjs(today).startOf("day"), [today]);

  const [value, setValue] = React.useState(dayjs(today));
  const [message, setMessage] = useState("");
  const [availabilityGrid, setAvailabilityGrid] = useState({});
  const [bookingGrid, setBookingGrid] = useState({});
  const [approvedGrid, setApprovedGrid] = useState({});
  const [hasApprovedData, setHasApprovedData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const CURRENT_KEY = "dashboard_current_view";

  const [user, setUser] = useState(null);
  const [fetchedData, setFetchedData] = useState(false);
  const [lockedSlots, setLockedSlots] = useState({});

  const [milestoneUsers, setMilestoneUsers] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(true);
  const [recipientsError, setRecipientsError] = useState("");
  const [recipientRetry, setRecipientRetry] = useState(0);
  const [pastWalkUsers, setPastWalkUsers] = useState([]);
  const [pastWalkLoading, setPastWalkLoading] = useState(false);
  const [pastWalkError, setPastWalkError] = useState("");
  const selectionUsers = [...milestoneUsers, ...pastWalkUsers];

  const [shareOption, setShareOption] = useState("all");
  const [isShareDropdownOpen, setIsShareDropdownOpen] = useState(false);
  const [showUserList, setShowUserList] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [userListTab, setUserListTab] = useState("both");

  useEffect(() => {
    if (!profile?.id) return;
    const controller = new AbortController();
    setPastWalkUsers([]);
    setPastWalkLoading(true);
    setPastWalkError("");
    apiClient.get(`/availability/getMilestoneTwoUsers/${profile.id}`, {
      params: { past_walk: true },
      signal: controller.signal,
    }).then(({ data }) => {
      if (controller.signal.aborted) return;
      if (!data?.status || !Array.isArray(data.data)) throw new Error("Invalid response");
      setPastWalkUsers(normalizeRecipients(data.data, profile.id));
    }).catch(() => {
      if (!controller.signal.aborted) setPastWalkError("Could not load past walk profiles. Please try again.");
    }).finally(() => {
      if (!controller.signal.aborted) setPastWalkLoading(false);
    });
    return () => controller.abort();
  }, [profile?.id, recipientRetry]);

  const [mySharedAvailability, setMySharedAvailability] = useState([]);
  const [isCurrentWeekLocked, setIsCurrentWeekLocked] = useState(false);

  useEffect(() => {
    function handleClickOutside(e) {
      if (!e.target.closest(".tpa-share-dropdown-wrap")) {
        setIsShareDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!profile?.id) return;
    const controller = new AbortController();
    setMilestoneUsers([]);
    setRecipientsLoading(true);
    setRecipientsError("");
    apiClient.get(`/availability/getMilestoneTwoUsers/${profile.id}`, {
      params: { team: "all" }, signal: controller.signal, timeout: 15000,
    }).then(({ data }) => {
      if (controller.signal.aborted) return;
      if (!data?.status) throw new Error("Recipients request failed");
      setMilestoneUsers(normalizeRecipients(data.data, profile.id));
    }).catch(() => {
      if (!controller.signal.aborted) setRecipientsError("Could not load trusted people. Please retry, or choose someone from Past Walks.");
    }).finally(() => {
      if (!controller.signal.aborted) setRecipientsLoading(false);
    });
    return () => controller.abort();
  }, [profile?.id, recipientRetry]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const res = await apiClient.get(`/user/details/${profile?.id}`);
        if (res.data?.status && res.data.data) setUser(res.data.data);
      } catch (err) {
        console.error("Error fetching user:", err);
      }
    };
    if (profile?.id) fetchUserDetails();
  }, [profile?.id]);

  useEffect(() => {
    if (!profile?.id) return;
    const fetchMyShared = async () => {
      try {
        const res = await apiClient.get(
          `/availability/get-Availability-Weeks-By-RequesterId/${profile.id}`,
        );
        if (res.data?.status && res.data.data) {
          setMySharedAvailability(res.data.data);
        }
      } catch (err) {
        console.error("Error fetching my shared availability:", err);
      }
    };
    fetchMyShared();
  }, [profile?.id]);

  const weekStartStr = useMemo(
    () => value.startOf("week").format("YYYY-MM-DD"),
    [value],
  );
  const weekEndStr = useMemo(
    () => value.endOf("week").format("YYYY-MM-DD"),
    [value],
  );

  useEffect(() => {
    setFetchedData(false);
    setLockedSlots({});
    setAvailabilityGrid({});
    setBookingGrid({});
    setApprovedGrid({});
    setHasApprovedData(false);
    setIsCurrentWeekLocked(false);

    // Strict boolean parser to prevent string "false" from triggering as true
    const isSlotActive = (val) => val === true || val === "true" || val === 1;

    const fetchCurrentWeekData = async () => {
      const realWorldCurrentWeekEnd = dayjs()
        .endOf("week")
        .format("YYYY-MM-DD");

      let effectiveWalkerId = walkerUserId;
      let bData = [];
      let appData = [];

      const matchingWeeks = mySharedAvailability.filter((w) => {
        const wEnd = dayjs(w.week_end).format("YYYY-MM-DD");
        return wEnd === weekEndStr;
      });

      let isLockedFromShared = false;

      if (matchingWeeks.length > 0) {
        isLockedFromShared = true;
        setIsCurrentWeekLocked(true);
        const firstMatch = matchingWeeks[0];
        const newGrid = {};
        const newLocked = {};

        firstMatch.days.forEach((d) => {
          const dayOfWeek = d.day_of_week;
          if (isSlotActive(d.morning)) {
            newGrid[`${dayOfWeek}-morning`] = true;
            newLocked[`${dayOfWeek}-morning`] = true;
          }
          if (isSlotActive(d.afternoon)) {
            newGrid[`${dayOfWeek}-afternoon`] = true;
            newLocked[`${dayOfWeek}-afternoon`] = true;
          }
          if (isSlotActive(d.evening)) {
            newGrid[`${dayOfWeek}-evening`] = true;
            newLocked[`${dayOfWeek}-evening`] = true;
          }
          if (isSlotActive(d.full_day)) {
            newGrid[`${dayOfWeek}-full`] = true;
            newLocked[`${dayOfWeek}-full`] = true;
          }
        });

        setAvailabilityGrid(newGrid);
        setLockedSlots(newLocked);
        setMessage(firstMatch.message || "");
        setShareOption(firstMatch.is_broadcast ? "all" : "selected");

        const walkers = [
          ...new Set(matchingWeeks.map((w) => w.walker_user_id)),
        ];
        setSelectedUserIds(recipientIds(walkers, profile.id));
        setShowUserList(false);
      } else {
        setIsCurrentWeekLocked(false);
        setMessage("");
        setShareOption("all");
        setSelectedUserIds([]);
      }

      if (profile?.id) {
        try {
          const bookRes = await apiClient.get(
            `/bookingWeeks/getBookingWeeksByUserId/${profile.id}`,
          );
          if (bookRes.data?.status && bookRes.data.data) {
            bData = bookRes.data.data;

            if (!effectiveWalkerId && !isLockedFromShared) {
              const matchingWeek = bData.find((bw) => {
                const bwEnd = dayjs(bw.week_end).format("YYYY-MM-DD");
                return bwEnd === weekEndStr;
              });
              if (matchingWeek) {
                effectiveWalkerId = matchingWeek.walker_user_id;
              }
            }
          }
        } catch (err) {
          console.error("Error fetching booking weeks:", err);
        }
      }

      // Fetch existing availability if applicable
      // if (effectiveWalkerId && !isLockedFromShared) {
      //   try {
      //     const res = await apiClient.get(
      //       `/availability/getAvailabilityByWalkerAndWeekStart?walker_user_id=${effectiveWalkerId}&week_start=${weekStartStr}`,
      //     );

      //     if (res.data?.status && res.data?.data) {
      //       const data = res.data.data;
      //       setFetchedData(true);

      //       const newGrid = {};
      //       const newLocked = {};

      //       data.days?.forEach((d) => {
      //         const dayOfWeek = d.day_of_week;
      //         if (isSlotActive(d.morning)) {
      //           newGrid[`${dayOfWeek}-morning`] = true;
      //           newLocked[`${dayOfWeek}-morning`] = true;
      //         }
      //         if (isSlotActive(d.afternoon)) {
      //           newGrid[`${dayOfWeek}-afternoon`] = true;
      //           newLocked[`${dayOfWeek}-afternoon`] = true;
      //         }
      //         if (isSlotActive(d.evening)) {
      //           newGrid[`${dayOfWeek}-evening`] = true;
      //           newLocked[`${dayOfWeek}-evening`] = true;
      //         }
      //         if (isSlotActive(d.full_day)) {
      //           newGrid[`${dayOfWeek}-full`] = true;
      //           newLocked[`${dayOfWeek}-full`] = true;
      //         }
      //       });

      //       setAvailabilityGrid(newGrid);
      //       setLockedSlots(newLocked);
      //     }
      //   } catch (err) {
      //     console.error("Error fetching availability:", err);
      //   }
      // }

      if (bData.length > 0) {
        const newBookingGrid = {};

        bData.forEach((bw) => {
          const bwEnd = dayjs(bw.week_end).format("YYYY-MM-DD");
          const isMatchPartner = bw.walker_user_id === effectiveWalkerId;
          const isCompatibleWeek = bwEnd === weekEndStr;
          const isNotExpired = dayjs(bwEnd).isSameOrAfter(
            realWorldCurrentWeekEnd,
            "day",
          );

          if (
            (!effectiveWalkerId || isMatchPartner) &&
            isCompatibleWeek &&
            isNotExpired
          ) {
            bw.days.forEach((d) => {
              const dayLabel = d.day_of_week;
              if (isSlotActive(d.morning)) {
                newBookingGrid[`${dayLabel}-morning`] = newBookingGrid[
                  `${dayLabel}-morning`
                ] || { count: 0, bw };
                newBookingGrid[`${dayLabel}-morning`].count += 1;
              }
              if (isSlotActive(d.afternoon)) {
                newBookingGrid[`${dayLabel}-afternoon`] = newBookingGrid[
                  `${dayLabel}-afternoon`
                ] || { count: 0, bw };
                newBookingGrid[`${dayLabel}-afternoon`].count += 1;
              }
              if (isSlotActive(d.evening)) {
                newBookingGrid[`${dayLabel}-evening`] = newBookingGrid[
                  `${dayLabel}-evening`
                ] || { count: 0, bw };
                newBookingGrid[`${dayLabel}-evening`].count += 1;
              }
              if (isSlotActive(d.full_day)) {
                newBookingGrid[`${dayLabel}-full`] = newBookingGrid[
                  `${dayLabel}-full`
                ] || { count: 0, bw };
                newBookingGrid[`${dayLabel}-full`].count += 1;
              }
            });
          }
        });

        setBookingGrid(newBookingGrid);
      }

      if (profile?.id) {
        try {
          const appRes = await apiClient.get(
            `/bookingWeeks/get-Approved-Booking-WeeksBy-UserId/${profile.id}`,
          );

          if (appRes.data?.status && appRes.data.data) {
            appData = appRes.data.data;
            let fetchedPartnerImg = userPlaceholder;

            if (effectiveWalkerId) {
              try {
                const pRes = await apiClient.get(
                  `/user/details/${effectiveWalkerId}`,
                );
                if (pRes.data?.data?.user_profile_image?.[0]) {
                  fetchedPartnerImg = pRes.data.data.user_profile_image[0];
                }
              } catch (e) {
                console.error("Could not fetch partner details for image", e);
              }
            }

            const newApprovedGrid = {};
            let foundApproved = false;

            appData.forEach((bw) => {
              const bwEnd = dayjs(bw.week_end).format("YYYY-MM-DD");
              const partnerOfBooking =
                bw.requester_user_id === profile.id
                  ? bw.walker_user_id
                  : bw.requester_user_id;
              const isMatchPartner = partnerOfBooking === effectiveWalkerId;
              const isCompatibleWeek = bwEnd === weekEndStr;
              const isNotExpired = dayjs(bwEnd).isSameOrAfter(
                realWorldCurrentWeekEnd,
                "day",
              );

              if (
                (!effectiveWalkerId || isMatchPartner) &&
                isCompatibleWeek &&
                isNotExpired
              ) {
                bw.days.forEach((d) => {
                  if (d.is_approved) {
                    foundApproved = true;
                    const dayLabel = d.day_of_week;
                    if (isSlotActive(d.morning))
                      newApprovedGrid[`${dayLabel}-morning`] =
                        fetchedPartnerImg;
                    if (isSlotActive(d.afternoon))
                      newApprovedGrid[`${dayLabel}-afternoon`] =
                        fetchedPartnerImg;
                    if (isSlotActive(d.evening))
                      newApprovedGrid[`${dayLabel}-evening`] =
                        fetchedPartnerImg;
                    if (isSlotActive(d.full_day))
                      newApprovedGrid[`${dayLabel}-full`] = fetchedPartnerImg;
                  }
                });
              }
            });

            setApprovedGrid(newApprovedGrid);
            setHasApprovedData(foundApproved);
          }
        } catch (appErr) {
          console.error("Error fetching approved weeks:", appErr);
        }
      }
    };

    fetchCurrentWeekData();
  }, [
    walkerUserId,
    profile?.id,
    value,
    mySharedAvailability,
    weekEndStr,
    weekStartStr,
  ]);

  const myDog = user?.dogs?.[0];
  const dogName = stateData.myDogName || myDog?.dog_name || "Dog";

  const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  const weekDaysList = ["MON", "TUE", "WED", "THU", "FRI"];
  const weekendList = ["SUN", "SAT"];
  const coreSlots = ["morning", "afternoon", "evening"];

  const timeSlotsUI = [
    {
      id: "morning",
      label: "MORNING",
      sub: "Before 12 noon",
      icon: (
        <img
          src={morningIconSvg}
          alt="Morning"
          style={{ width: 20, height: 20 }}
        />
      ),
    },
    {
      id: "afternoon",
      label: "AFTERNOON",
      sub: "12 noon- 5 pm",
      icon: (
        <img
          src={afternoonIconSvg}
          alt="Afternoon"
          style={{ width: 20, height: 20 }}
        />
      ),
    },
    {
      id: "evening",
      label: "EVENING",
      sub: "After 5 pm",
      icon: (
        <img
          src={eveningIconSvg}
          alt="Evening"
          style={{ width: 20, height: 20 }}
        />
      ),
    },
    { id: "full", label: "FULL DAY", sub: "+ Sleepover", icon: null },
  ];

  const validDays = days.filter((day, index) => {
    const dateForDay = value.startOf("week").add(index, "day");
    return !dateForDay.isBefore(todayDayjs, "day");
  });

  const validWeekDays = weekDaysList.filter((day) => validDays.includes(day));
  const validWeekends = weekendList.filter((day) => validDays.includes(day));

  const isDayFull = (day) => !!availabilityGrid[`${day}-full`];
  const isDayTouched = (day) =>
    ["morning", "afternoon", "evening", "full"].some(
      (slot) => availabilityGrid[`${day}-${slot}`],
    );

  const isAllSelected =
    validDays.length > 0 && validDays.every((day) => isDayFull(day));
  const areWeekdaysFull =
    validWeekDays.length > 0 && validWeekDays.every((day) => isDayFull(day));
  const isWeekdaysBulkActive =
    areWeekdaysFull && !validWeekends.some((day) => isDayTouched(day));
  const areWeekendsFull =
    validWeekends.length > 0 && validWeekends.every((day) => isDayFull(day));
  const isWeekendsBulkActive =
    areWeekendsFull && !validWeekDays.some((day) => isDayTouched(day));

  const toggleCell = (day, slotId) => {
    const cellKey = `${day}-${slotId}`;

    // 1. Navigation routing if it is a fetched booked slot
    const bookingData = bookingGrid[cellKey];
    if (bookingData && bookingData.bw) {
      const dayIndex = days.indexOf(day);
      const dateForDay = value.startOf("week").add(dayIndex, "day");
      navigate("/availability-request", {
        state: {
          partnerId: bookingData.bw.walker_user_id || walkerUserId,
          dogName,
          partnerName,
          bookingId: bookingData.bw.id || bookingId,
          selectedDate: dateForDay.toISOString(),
        },
      });
      return;
    }

    // 2. Lock check for the rest of the unbooked slots
    const hasBookingsThisWeek = Object.keys(bookingGrid).length > 0;
    if (hasBookingsThisWeek || hasApprovedData || isCurrentWeekLocked) return;

    // 3. Prevent past days interaction
    const dayIndex = days.indexOf(day);
    const dateForDay = value.startOf("week").add(dayIndex, "day");
    if (dateForDay.isBefore(todayDayjs, "day")) return;

    if (fetchedData) {
      navigate("/availability-request", {
        state: {
          partnerId: walkerUserId,
          dogName,
          partnerName,
          bookingId: bookingId,
          selectedDate: dateForDay.toISOString(),
        },
      });
      return;
    }

    // 4. Update state logic manually
    const newGrid = { ...availabilityGrid };
    if (slotId === "full") {
      const currentlyFull = isDayFull(day);
      const targetState = !currentlyFull;

      newGrid[`${day}-full`] = targetState;
      if (targetState) {
        coreSlots.forEach((s) => {
          newGrid[`${day}-${s}`] = true;
        });
      } else {
        coreSlots.forEach((s) => {
          newGrid[`${day}-${s}`] = false;
        });
      }
    } else {
      const targetState = !newGrid[cellKey];
      newGrid[cellKey] = targetState;

      // If user unchecks a sub-slot, break the full-day
      if (!targetState) {
        newGrid[`${day}-full`] = false;
      }
    }
    setAvailabilityGrid(newGrid);
  };

  const handleBulkSelect = (type) => {
    const hasBookingsThisWeek = Object.keys(bookingGrid).length > 0;
    if (
      fetchedData ||
      hasApprovedData ||
      isCurrentWeekLocked ||
      hasBookingsThisWeek
    )
      return;

    let newGrid = { ...availabilityGrid };
    const allSlots = [...coreSlots, "full"];

    if (type === "all") {
      const targetState = !isAllSelected;
      validDays.forEach((day) =>
        allSlots.forEach((slot) => (newGrid[`${day}-${slot}`] = targetState)),
      );
    } else if (type === "weekdays") {
      const targetState = !isWeekdaysBulkActive;
      validWeekDays.forEach((day) =>
        allSlots.forEach((slot) => (newGrid[`${day}-${slot}`] = targetState)),
      );
      if (targetState) {
        validWeekends.forEach((day) =>
          allSlots.forEach((slot) => (newGrid[`${day}-${slot}`] = false)),
        );
      }
    } else if (type === "weekends") {
      const targetState = !isWeekendsBulkActive;
      validWeekends.forEach((day) =>
        allSlots.forEach((slot) => (newGrid[`${day}-${slot}`] = targetState)),
      );
      if (targetState) {
        validWeekDays.forEach((day) =>
          allSlots.forEach((slot) => (newGrid[`${day}-${slot}`] = false)),
        );
      }
    }
    setAvailabilityGrid(newGrid);
  };

  const toggleUserSelection = (id) => {
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const filteredMilestoneUsers = userListTab === "past" ? pastWalkUsers : milestoneUsers.filter((u) => {
    if (userListTab === "both") return true;
    if (userListTab === "pups") return u.team === "paws";
    if (userListTab === "pals") return u.team === "social";
    return true;
  });

  const getTargetWalkerIds = () => {
    if (shareOption === "none") {
      const pid = stateData.partnerId || walkerUserId;
      if (!pid || pid === "all") return [];
      return recipientIds([pid], profile.id);
    }
    if (shareOption === "selected") {
      return recipientIds(selectedUserIds, profile.id);
    }
    if (shareOption === "all") {
      return recipientIds(milestoneUsers.map((u) => u.user_id), profile.id);
    }
    return [];
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (!myDog?.id || !profile?.id) {
      triggerAlert("Missing required booking data.", "error");
      return;
    }
    if (!hasAnySelection) {
      triggerAlert("Please select at least one availability slot.", "error");
      return;
    }

    if (shareOption === "all" && (recipientsLoading || recipientsError)) {
      triggerAlert(recipientsLoading ? "Please wait while trusted people load." : "Trusted people could not load. Please retry or choose specific people.", "error");
      return;
    }
    const targetWalkerIds = getTargetWalkerIds();
    if (targetWalkerIds.length === 0) {
      triggerAlert(
        shareOption === "all" ? "No trusted people are available. Choose specific people and check Past Walks." : "Choose at least one person to share availability with.",
        "error",
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const weekStart = value.startOf("week").format("YYYY-MM-DD");
      const weekEnd = value.endOf("week").format("YYYY-MM-DD");
      const daysList = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

      const formattedDays = daysList
        .map((dayLabel, index) => {
          const dateForDay = value
            .startOf("week")
            .add(index, "day")
            .format("YYYY-MM-DD");
          return {
            date: dateForDay,
            day_of_week: dayLabel,
            morning: !!availabilityGrid[`${dayLabel}-morning`],
            afternoon: !!availabilityGrid[`${dayLabel}-afternoon`],
            evening: !!availabilityGrid[`${dayLabel}-evening`],
            full_day: !!availabilityGrid[`${dayLabel}-full`],
          };
        })
        .filter((d) => d.morning || d.afternoon || d.evening || d.full_day);

      const payload = {
        requester_user_id: profile.id,
        walker_user_id: targetWalkerIds,
        dog_id: myDog.id,
        week_start: weekStart,
        week_end: weekEnd,
        message: message,
        isBroadcast: shareOption === "all",
        days: formattedDays,
      };

      const response = await apiClient.post(
        "/availability/createAvailabilityWeek",
        payload,
      );

      if (response.data?.status) {
        triggerAlert("Availability submitted successfully!", "success");
        setShowSuccessModal(true);
      } else {
        triggerAlert(
          response.data?.message || "Failed to submit availability.",
          "error",
        );
      }
    } catch (error) {
      triggerAlert(
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong.",
        "error",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    if (bookingId) {
      navigate("/current-bookings", { state: { returnId: bookingId } });
    } else {
      localStorage.setItem(CURRENT_KEY, "map");
      navigate("/dashboard-switcher");
    }
  };

  const hasAnySelection = useMemo(() => {
    return Object.values(availabilityGrid).some((val) => val === true);
  }, [availabilityGrid]);

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const years = Array.from({ length: 10 }, (_, i) => dayjs().year() + i);

  const prevWeek = () => setValue(value.subtract(1, "week"));
  const nextWeek = () => setValue(value.add(1, "week"));

  const activeDays = useMemo(() => {
    return days
      .map((day, index) => {
        const dateForDay = value.startOf("week").add(index, "day");
        const mSelected = availabilityGrid[`${day}-morning`];
        const mBooked = bookingGrid[`${day}-morning`];
        const mApproved = approvedGrid[`${day}-morning`];
        const aSelected = availabilityGrid[`${day}-afternoon`];
        const aBooked = bookingGrid[`${day}-afternoon`];
        const aApproved = approvedGrid[`${day}-afternoon`];
        const eSelected = availabilityGrid[`${day}-evening`];
        const eBooked = bookingGrid[`${day}-evening`];
        const eApproved = approvedGrid[`${day}-evening`];

        const hasMorning = mSelected || mBooked || mApproved;
        const hasAfternoon = aSelected || aBooked || aApproved;
        const hasEvening = eSelected || eBooked || eApproved;

        if (hasMorning || hasAfternoon || hasEvening) {
          return {
            day,
            date: dateForDay,
            morning: hasMorning
              ? { selected: mSelected, booked: mBooked, approved: mApproved }
              : null,
            afternoon: hasAfternoon
              ? { selected: aSelected, booked: aBooked, approved: aApproved }
              : null,
            evening: hasEvening
              ? { selected: eSelected, booked: eBooked, approved: eApproved }
              : null,
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [availabilityGrid, bookingGrid, approvedGrid, value]);

  const renderSmallSlot = (slotData, type) => {
    if (!slotData) return null;

    let content;
    let cellClass = "tpa-small-grid-cell";
    let iconSize = 20;

    if (slotData.approved) {
      cellClass += " tpa-small-cell-approved";
      content = (
        <img
          src={slotData.approved}
          alt="Approved"
          className="tpa-small-approved-img"
        />
      );
    } else if (slotData.booked) {
      cellClass += " tpa-small-cell-booked";
      content = (
        <>
          {type === "morning" && (
            <img
              src={morningIconSvg}
              alt="Morning"
              className="tpa-small-icon"
              style={{ width: iconSize, height: iconSize }}
            />
          )}
          {type === "afternoon" && (
            <img
              src={afternoonIconSvg}
              alt="Afternoon"
              className="tpa-small-icon"
              style={{ width: iconSize, height: iconSize }}
            />
          )}
          {type === "evening" && (
            <img
              src={eveningIconSvg}
              alt="Evening"
              className="tpa-small-icon"
              style={{ width: iconSize, height: iconSize }}
            />
          )}
          <div className="tpa-small-booking-badge">{slotData.booked.count}</div>
        </>
      );
    } else {
      cellClass += " tpa-small-cell-selected";
      content = (
        <>
          {type === "morning" && (
            <img
              src={morningIconSvg}
              alt="Morning"
              className="tpa-small-icon"
              style={{ width: iconSize, height: iconSize }}
            />
          )}
          {type === "afternoon" && (
            <img
              src={afternoonIconSvg}
              alt="Afternoon"
              className="tpa-small-icon"
              style={{ width: iconSize, height: iconSize }}
            />
          )}
          {type === "evening" && (
            <img
              src={eveningIconSvg}
              alt="Evening"
              className="tpa-small-icon"
              style={{ width: iconSize, height: iconSize }}
            />
          )}
        </>
      );
    }

    return <div className={cellClass}>{content}</div>;
  };

  const hasBookingsThisWeek = Object.keys(bookingGrid).length > 0;
  const isWeekGloballyLocked =
    isCurrentWeekLocked ||
    hasBookingsThisWeek ||
    hasApprovedData ||
    fetchedData;

  return (
    <div className="tpa-container">
      <div className="tpa-custom-top-header tpa-desktop-only">
        <div
          className="tpa-custom-brand"
          onClick={() => navigate("/dashboard-switcher")}
        >
          <img
            src={appIcon}
            alt="Social Paws"
            style={{ width: "32px", height: "32px", objectFit: "contain" }}
          />
          <span>Social Paws</span>
        </div>
        <div className="tpa-header-icons-right">
          <TopNavBar theme="light" />
        </div>
      </div>

      <div
        className="cb-custom-top-header tpa-mobile-only"
        style={{ background: "transparent", marginBottom: "-2rem", zIndex: 10 }}
      >
        <MobileTopBar
          onBack={() => navigate("/dashboard-switcher")}
          backColor="#000"
          logoColor="#009fd9"
          blueLogo="true"
        />
        <div className="cb-header-bottom-row">
          <div className="header-icons-right">
            <TopNavBar theme="light" />
          </div>
        </div>
      </div>

      <div className="tpa-scroll-wrapper">
        <div className="tpa-content">
          <h1 className="tpa-title">{dogName}'S AVAILABILITY</h1>
          <p className="tpa-subtitle">
            Share {dogName}’s availability so Pup Pals can book time with{" "}
            {dogName} based on your schedule and preferences!
          </p>
          <p className="tpa-subtitle" style={{ marginBottom: "1.5rem" }}>
            Simply select a week of the month and select as many time slots as
            you’d like for sharing {dogName}.
          </p>

          <div className="tpa-top-controls">
            <div className="tpa-dropdown-container">
              <select
                value={value.month()}
                onChange={(e) => setValue(value.month(Number(e.target.value)))}
              >
                {months.map((m, i) => (
                  <option key={m} value={i}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
            <div className="tpa-dropdown-container">
              <select
                value={value.year()}
                onChange={(e) => setValue(value.year(Number(e.target.value)))}
              >
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="tpa-split-layout">
            <div className="tpa-left-col tpa-planner-container">
              <h3 className="tpa-planner-title">Weekly-Planner</h3>

              <div className="tpa-week-nav">
                <button onClick={prevWeek}>
                  <ChevronLeft size={20} />
                </button>
                <span>
                  {value.startOf("week").format("ddd MMM D")} -{" "}
                  {value.endOf("week").format("ddd MMM D")}
                </span>
                <button onClick={nextWeek}>
                  <ChevronRight size={20} />
                </button>
              </div>
              <div className="tpa-planner-subtitle">Select Slots here</div>

              <div className="tpa-grid-header-row">
                <div className="tpa-empty-cell"></div>
                {timeSlotsUI.map((slot) => (
                  <div key={slot.id} className="tpa-header-slot-label">
                    <span
                      className={
                        slot.id === "full"
                          ? "tpa-slot-title-last"
                          : "tpa-slot-title"
                      }
                    >
                      {slot.label}
                    </span>
                    <span
                      className={
                        slot.id === "full"
                          ? "tpa-slot-sub-last"
                          : "tpa-slot-sub"
                      }
                    >
                      {slot.sub}
                    </span>
                  </div>
                ))}
              </div>

              <div className="tpa-grid-body">
                {days.map((day, index) => {
                  const dateForDay = value.startOf("week").add(index, "day");
                  const isPastDay = dateForDay.isBefore(todayDayjs, "day");

                  const fullKey = `${day}-full`;
                  const isFullBooked = bookingGrid[fullKey]?.count > 0;
                  const isFullApproved = !!approvedGrid[fullKey];
                  const dayIsFull = isDayFull(day);

                  // Strictly derived from explicit logic. No implicit associations!
                  const showFullChecked =
                    dayIsFull || isFullBooked || isFullApproved;

                  return (
                    <div
                      key={day}
                      className="tpa-grid-row"
                      style={{
                        opacity: isPastDay ? 0.4 : 1,
                        pointerEvents: isPastDay ? "none" : "auto",
                      }}
                    >
                      <div className="tpa-day-label">
                        {day}, {dateForDay.format("DD")}
                      </div>

                      {["morning", "afternoon", "evening"].map((slot) => {
                        const cellKey = `${day}-${slot}`;
                        const isSelected = availabilityGrid[cellKey];
                        const bookingData = bookingGrid[cellKey];
                        const bookingCount = bookingData
                          ? bookingData.count
                          : 0;
                        const isBooked = bookingCount > 0;
                        const isApproved = !!approvedGrid[cellKey];
                        const partnerImg = approvedGrid[cellKey];

                        let cellClass = "tpa-grid-cell tpa-icon-cell";
                        if (isApproved) cellClass += " tpa-cell-approved";
                        else if (isBooked) cellClass += " tpa-cell-booked";
                        else if (isSelected) cellClass += " tpa-cell-selected";
                        else cellClass += " tpa-cell-unselected";

                        if (isCurrentWeekLocked && isSelected) {
                          cellClass += " tpa-cell-locked";
                        }

                        return (
                          <div
                            key={slot}
                            className={cellClass}
                            onClick={() => toggleCell(day, slot)}
                            style={{
                              position: "relative",
                              cursor: isWeekGloballyLocked
                                ? isBooked
                                  ? "pointer"
                                  : "default"
                                : "pointer",
                              opacity:
                                isWeekGloballyLocked &&
                                !isBooked &&
                                !isSelected &&
                                !isApproved
                                  ? 0.6
                                  : 1,
                            }}
                          >
                            {isApproved ? (
                              <img
                                src={partnerImg}
                                alt="Approved"
                                className="tpa-approved-img"
                              />
                            ) : (
                              <>
                                {slot === "morning" && (
                                  <img
                                    src={morningIconSvg}
                                    alt="Morning"
                                    className="tpa-cell-icon"
                                    style={{ width: 30, height: 30 }}
                                  />
                                )}
                                {slot === "afternoon" && (
                                  <img
                                    src={afternoonIconSvg}
                                    alt="Afternoon"
                                    className="tpa-cell-icon"
                                    style={{ width: 30, height: 30 }}
                                  />
                                )}
                                {slot === "evening" && (
                                  <img
                                    src={eveningIconSvg}
                                    alt="Evening"
                                    className="tpa-cell-icon"
                                    style={{ width: 30, height: 30 }}
                                  />
                                )}
                              </>
                            )}

                            {isBooked && !isApproved && (
                              <div className="tpa-booking-badge">
                                {bookingCount}
                              </div>
                            )}
                          </div>
                        );
                      })}

                      <div
                        className={`tpa-bulk-checkbox ${showFullChecked ? "checked" : ""}`}
                        onClick={() => toggleCell(day, "full")}
                        style={{
                          margin: "0 auto",
                          cursor: isWeekGloballyLocked
                            ? isFullBooked
                              ? "pointer"
                              : "default"
                            : "pointer",
                          opacity:
                            isWeekGloballyLocked && !showFullChecked ? 0.6 : 1,
                        }}
                      >
                        {showFullChecked && <Check size={24} />}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div
                className="tpa-bulk-select-row"
                style={{
                  opacity: isWeekGloballyLocked ? 0.6 : 1,
                  pointerEvents: isWeekGloballyLocked ? "none" : "auto",
                }}
              >
                <div
                  className="tpa-bulk-item"
                  onClick={() => handleBulkSelect("all")}
                >
                  <span>SELECT ALL</span>
                  <div
                    className={`tpa-bulk-checkbox ${isAllSelected ? "checked" : ""}`}
                  >
                    {isAllSelected && <Check size={20} />}
                  </div>
                </div>
                <div
                  className="tpa-bulk-item"
                  onClick={() => handleBulkSelect("weekdays")}
                >
                  <span>WEEKDAYS</span>
                  <div
                    className={`tpa-bulk-checkbox ${isWeekdaysBulkActive ? "checked" : ""}`}
                  >
                    {isWeekdaysBulkActive && <Check size={20} />}
                  </div>
                </div>
                <div
                  className="tpa-bulk-item"
                  onClick={() => handleBulkSelect("weekends")}
                >
                  <span>WEEKENDS</span>
                  <div
                    className={`tpa-bulk-checkbox ${isWeekendsBulkActive ? "checked" : ""}`}
                  >
                    {isWeekendsBulkActive && <Check size={20} />}
                  </div>
                </div>
              </div>
            </div>

            <div className="tpa-right-col">
              <div className="tpa-right-section">
                <h3 className="tpa-section-label">
                  {activeDays.length > 0 ? "Your Active Slots" : "Active Slots"}
                </h3>

                {activeDays.length === 0 ? (
                  <div className="tpa-no-active-slots">
                    <p>No active slots.</p>
                    <p className="tpa-hint-text">
                      Select a slot in the week-planner on the left, to make it
                      available.
                    </p>
                  </div>
                ) : (
                  <div className="tpa-active-slots-container">
                    <div className="tpa-active-month-chip">
                      {value.format("MMM YYYY")}
                    </div>
                    <div className="tpa-active-days-wrap">
                      {activeDays.map((d) => (
                        <div key={d.day} className="tpa-active-day-card">
                          <div className="tpa-active-day-title">
                            {d.day.charAt(0).toUpperCase() +
                              d.day.slice(1).toLowerCase()}
                            , {d.date.format("DD")}
                          </div>
                          <div className="tpa-active-day-slots">
                            {renderSmallSlot(d.morning, "morning")}
                            {renderSmallSlot(d.afternoon, "afternoon")}
                            {renderSmallSlot(d.evening, "evening")}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {!(fetchedData || hasApprovedData) && (
                <div className="tpa-right-section tpa-share-section">
                  <h3 className="tpa-section-label">Share Availability with</h3>

                  <div className="tpa-share-dropdown-wrap">
                    <div
                      className={`tpa-custom-dropdown-trigger ${isShareDropdownOpen ? "open" : ""} ${shareOption ? "has-value" : ""} ${isCurrentWeekLocked ? "disabled" : ""}`}
                      onClick={() => {
                        if (!isCurrentWeekLocked)
                          setIsShareDropdownOpen(!isShareDropdownOpen);
                      }}
                      style={{
                        cursor: isCurrentWeekLocked ? "default" : "pointer",
                        opacity: isCurrentWeekLocked ? 0.7 : 1,
                      }}
                    >
                      <span
                        style={{
                          fontSize: "inherit",
                          color: "inherit",
                          userSelect: "none",
                        }}
                      >
                        {shareOptionsMap[shareOption] || "Select..."}
                      </span>
                      {isShareDropdownOpen ? (
                        <ChevronUp size={20} />
                      ) : (
                        <ChevronDown size={20} />
                      )}
                    </div>

                    {isShareDropdownOpen && !isCurrentWeekLocked && (
                      <div className="tpa-custom-dropdown-menu">
                        {Object.entries(shareOptionsMap)
                          .filter(([val]) => val !== "none" || walkerUserId)
                          .map(([val, label]) => (
                            <div
                              key={val}
                              className="tpa-custom-dropdown-option"
                              onClick={() => {
                                setShareOption(val);
                                if (val === "selected") {
                                  setShowUserList(true);
                                } else {
                                  setShowUserList(false);
                                }
                                setIsShareDropdownOpen(false);
                              }}
                            >
                              {label}
                            </div>
                          ))}
                      </div>
                    )}

                    {!isCurrentWeekLocked &&
                      shareOption === "selected" &&
                      !showUserList && (
                        <button
                          className="tpa-edit-share-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowUserList(true);
                          }}
                        >
                          EDIT
                        </button>
                      )}
                  </div>

                  {!isCurrentWeekLocked && (
                    <div className="tpa-recipient-help">
                      {shareOption === "all" && <>
                        <button type="button" onClick={() => { setShareOption("selected"); setShowUserList(true); }}>Choose specific people</button>
                        <p role="status">{recipientsLoading ? "Loading trusted people…" : recipientsError || (milestoneUsers.length ? `Availability will be shared with ${milestoneUsers.length} trusted people.` : "No trusted people found. Choose specific people to check Past Walks.")}</p>
                      </>}
                      {recipientsError && <button type="button" onClick={() => setRecipientRetry((value) => value + 1)}>Retry loading people</button>}
                    </div>
                  )}

                  {!isCurrentWeekLocked &&
                    shareOption === "selected" &&
                    showUserList && (
                      <div className="tpa-user-selection-box">
                        <div className="tpa-us-header">
                          <span className="tpa-us-title">Select below</span>
                          <div className="tpa-us-tabs">
                            <span
                              className={userListTab === "past" ? "active" : ""}
                              onClick={() => setUserListTab("past")}
                            >
                              Past Walks
                            </span>
                            <span
                              className={userListTab === "both" ? "active" : ""}
                              onClick={() => setUserListTab("both")}
                            >
                              Show Both
                            </span>
                            <span
                              className={userListTab === "pups" ? "active" : ""}
                              onClick={() => setUserListTab("pups")}
                            >
                              Trusted Pups
                            </span>
                            <span
                              className={userListTab === "pals" ? "active" : ""}
                              onClick={() => setUserListTab("pals")}
                            >
                              Trusted Pup Pals
                            </span>
                          </div>
                        </div>

                        <div className="tpa-user-list-content">
                          <div className="tpa-user-list">
                            {userListTab === "past" && pastWalkLoading && <p role="status">Loading past walks…</p>}
                            {userListTab === "past" && pastWalkError && <p role="alert">{pastWalkError}</p>}
                            {userListTab === "past" && !pastWalkLoading && !pastWalkError && pastWalkUsers.length === 0 && <p>No past walk profiles yet.</p>}
                            {userListTab !== "past" && recipientsLoading && <p role="status">Loading trusted people…</p>}
                            {userListTab !== "past" && recipientsError && <p role="alert">{recipientsError}</p>}
                            {userListTab !== "past" && !recipientsLoading && !recipientsError && filteredMilestoneUsers.length === 0 && <p>No trusted people in this category. Try Past Walks.</p>}
                            {userListTab === "past" && pastWalkError && <button type="button" onClick={() => setRecipientRetry((value) => value + 1)}>Retry past walks</button>}
                            {filteredMilestoneUsers.map((u) => {
                              const avatarImg =
                                u.user_profile_image?.[0] || userPlaceholder;
                              return (
                                <div
                                  key={u.user_id}
                                  className="tpa-user-list-item"
                                  role="checkbox"
                                  aria-checked={selectedUserIds.includes(u.user_id)}
                                  tabIndex={0}
                                  onKeyDown={(event) => { if (event.key === " " || event.key === "Enter") { event.preventDefault(); toggleUserSelection(u.user_id); } }}
                                  onClick={() => toggleUserSelection(u.user_id)}
                                >
                                  <div
                                    className={`tpa-user-checkbox ${selectedUserIds.includes(u.user_id) ? "checked" : ""}`}
                                  >
                                    {selectedUserIds.includes(u.user_id) && (
                                      <Check size={16} strokeWidth={3} />
                                    )}
                                  </div>
                                  <img
                                    src={avatarImg}
                                    alt={u.user_name}
                                    className="tpa-us-avatar"
                                  />
                                  {u.team === "paws" ? (
                                    <Bone
                                      size={16}
                                      strokeWidth={2}
                                      style={{ transform: "rotate(45deg)" }}
                                    />
                                  ) : (
                                    <Heart size={16} strokeWidth={2} />
                                  )}
                                  <span className="tpa-us-name">
                                    {u.user_name}
                                  </span>
                                  <span className="tpa-us-distance">
                                    {u.user_city || "Unknown"}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                          <button
                            className="tpa-us-done-btn"
                            onClick={() => setShowUserList(false)}
                          >
                            DONE
                          </button>
                        </div>
                      </div>
                    )}

                  {shareOption === "selected" &&
                    !showUserList &&
                    selectedUserIds.length > 0 && (
                      <div className="tpa-selected-chips-container">
                        {selectedUserIds.map((id) => {
                          const u = selectionUsers.find(
                            (mu) => mu.user_id === id,
                          );
                          if (!u) return null;
                          const avatarImg =
                            u.user_profile_image?.[0] || userPlaceholder;
                          return (
                            <div key={id} className="tpa-selected-chip">
                              <img src={avatarImg} alt={u.user_name} />
                              <span>{u.user_name}</span>
                              {!isCurrentWeekLocked && (
                                <button onClick={() => toggleUserSelection(id)}>
                                  <X size={16} />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                </div>
              )}

              <div className="tpa-right-section tpa-message-section">
                <h3 className="tpa-section-label">Your Message</h3>
                <textarea
                  className="tpa-message-input"
                  placeholder="Enter here"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  readOnly={isCurrentWeekLocked}
                  style={{ opacity: isCurrentWeekLocked ? 0.7 : 1 }}
                />
              </div>

              {!(fetchedData || hasApprovedData || isCurrentWeekLocked) && (
                <button
                  className={`tpa-submit-btn ${!hasAnySelection || isSubmitting ? "disabled" : ""}`}
                  onClick={handleSubmit}
                  disabled={!hasAnySelection || isSubmitting}
                >
                  {isSubmitting ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    "SUBMIT"
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Submit Summary Modal */}
      {showSuccessModal && (
        <div className="tpa-modal-overlay">
          <div className="tpa-modal-content">
            <button className="tpa-modal-close" onClick={handleCloseModal}>
              <X size={20} color="#cc0000" strokeWidth={3} />
            </button>
            <h2 className="tpa-modal-title">
              {dogName.toUpperCase()}'S AVAILABILITY UPDATED
            </h2>

            <div className="tpa-modal-section">
              <h4 className="tpa-modal-section-title">Active Slots</h4>
              <div className="tpa-active-slots-container">
                <div className="tpa-active-month-chip tpa-modal-chip">
                  {value.format("MMM YYYY")}
                </div>
                <div className="tpa-active-days-wrap-modal">
                  {activeDays.map((d) => (
                    <div
                      key={d.day}
                      className="tpa-active-day-card-modal tpa-modal-day-card"
                    >
                      <div className="tpa-active-day-title">
                        {d.day.charAt(0).toUpperCase() +
                          d.day.slice(1).toLowerCase()}
                        , {d.date.format("DD")}
                      </div>
                      <div className="tpa-active-day-slots">
                        {renderSmallSlot(d.morning, "morning")}
                        {renderSmallSlot(d.afternoon, "afternoon")}
                        {renderSmallSlot(d.evening, "evening")}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="tpa-modal-section" style={{ marginTop: "1rem" }}>
              <h4 className="tpa-modal-section-title">
                Sharing Availability with
              </h4>
              <div className="tpa-selected-chips-container">
                {shareOption === "all" ? (
                  <div className="tpa-selected-chip tpa-modal-user-chip">
                    <span>All Trusted Pup Pals/ Pups</span>
                  </div>
                ) : shareOption === "none" ? (
                  <div className="tpa-selected-chip tpa-modal-user-chip">
                    <span>No one</span>
                  </div>
                ) : (
                  selectedUserIds.map((id) => {
                    const u = selectionUsers.find((mu) => mu.user_id === id);
                    if (!u) return null;
                    const avatarImg =
                      u.user_profile_image?.[0] || userPlaceholder;
                    return (
                      <div
                        key={id}
                        className="tpa-selected-chip tpa-modal-user-chip"
                      >
                        <img src={avatarImg} alt={u.user_name} />
                        <span>{u.user_name}</span>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="tpa-modal-section" style={{ marginTop: "1rem" }}>
              <h4 className="tpa-modal-section-title">Your Message</h4>
              <div className="tpa-modal-message-box">
                {message ? `"${message}"` : "No message provided."}
              </div>
            </div>

            <button
              className="tpa-modal-explore-btn"
              onClick={handleCloseModal}
            >
              GO TO EXPLORE
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamPawsAvailabilityPage;
